/**
 * Affiliate payout methods and validation (pure; shared by the API, /earn and Admin). Same countries, methods,
 * fields and checks as OutlierOS (docs/legacy-ui/03 §12). Payouts are made by hand (decision 16).
 */

export const PAYOUT_METHODS = ['upi', 'bank', 'wise', 'amazon_gift_card'] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

export interface PayoutField { key: string; label: string; placeholder?: string; optional?: boolean }

export const PAYOUT_METHOD_LABELS: Record<PayoutMethod, string> = {
  upi: 'UPI (India)',
  bank: 'Bank transfer',
  wise: 'Wise',
  amazon_gift_card: 'Amazon.com gift card',
};

/** Countries whose banks use IBAN. */
const IBAN_COUNTRIES = new Set('AD AE AL AT AZ BA BE BG BH BR CH CR CY CZ DE DK DO EE EG ES FI FO FR GB GE GI GL GR GT HR HU IE IL IQ IS IT JO KW KZ LB LC LI LT LU LV MC MD ME MK MR MT MU NL NO PK PL PS PT QA RO RS SA SC SE SI SK SM TL TN TR UA VA VG XK'.split(' '));

/** Common payout countries first; the form adds the rest, named by Intl.DisplayNames. */
export const PRIORITY_COUNTRIES = ['IN', 'US', 'GB', 'AE', 'CA', 'AU', 'SG', 'DE', 'FR', 'NL', 'ES', 'IT', 'PK', 'BD', 'NP', 'LK', 'PH', 'ID', 'MY', 'NG', 'KE', 'ZA', 'BR', 'MX'];
export const ALL_COUNTRIES = 'AD AE AF AG AI AL AM AO AR AT AU AW AZ BA BB BD BE BF BG BH BI BJ BM BN BO BR BS BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR CV CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM FO FR GA GB GD GE GH GI GL GM GN GQ GR GT GW GY HK HN HR HT HU ID IE IL IN IQ IS IT JM JO JP KE KG KH KI KM KN KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MH MK ML MM MN MO MR MS MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NR NZ OM PA PE PG PH PK PL PS PT PW PY QA RO RS RW SA SB SC SE SG SI SK SL SM SN SO SR SS ST SV SZ TC TD TG TH TJ TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VN VU WS XK YE ZA ZM ZW'.split(' ');

export function methodsForCountry(country: string): PayoutMethod[] {
  return country === 'IN' ? ['upi', 'bank', 'wise', 'amazon_gift_card'] : ['bank', 'wise', 'amazon_gift_card'];
}

/** Form fields for a method in a country (bank fields follow local conventions). */
export function payoutFields(method: PayoutMethod, country: string): PayoutField[] {
  const bankName: PayoutField = { key: 'bank_name', label: 'Bank name' };
  switch (method) {
    case 'upi': return [{ key: 'upi_id', label: 'UPI ID', placeholder: 'name@okaxis' }];
    case 'wise': return [{ key: 'email', label: 'Wise account email', placeholder: 'you@example.com' }];
    case 'amazon_gift_card': return [{ key: 'email', label: 'Email to receive the Amazon.com gift card', placeholder: 'you@example.com' }];
    case 'bank':
      if (country === 'IN') return [{ key: 'account_number', label: 'Account number' }, { key: 'ifsc', label: 'IFSC code', placeholder: 'HDFC0001234' }, bankName];
      if (country === 'US') return [{ key: 'account_number', label: 'Account number' }, { key: 'routing_number', label: 'ABA routing number (9 digits)' }, { key: 'account_type', label: 'Account type (checking / savings)', placeholder: 'checking' }, bankName];
      if (country === 'GB') return [{ key: 'account_number', label: 'Account number (8 digits)' }, { key: 'sort_code', label: 'Sort code (6 digits)', placeholder: '12-34-56' }, bankName];
      if (country === 'AU') return [{ key: 'account_number', label: 'Account number' }, { key: 'bsb', label: 'BSB (6 digits)' }, bankName];
      if (country === 'CA') return [{ key: 'account_number', label: 'Account number' }, { key: 'transit_number', label: 'Transit number (5 digits)' }, { key: 'institution_number', label: 'Institution number (3 digits)' }, bankName];
      if (IBAN_COUNTRIES.has(country)) return [{ key: 'iban', label: 'IBAN' }, { key: 'swift_bic', label: 'SWIFT / BIC', optional: true }, bankName];
      return [{ key: 'account_number', label: 'Account number or IBAN' }, { key: 'swift_bic', label: 'SWIFT / BIC' }, { key: 'bank_address', label: 'Bank address', optional: true }, bankName];
  }
}

const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,255}\.[A-Za-z]{2,}$/;
const UPI = /^[A-Za-z0-9._-]{2,256}@[A-Za-z][A-Za-z0-9.-]{1,64}$/;

export function isValidIban(raw: string): boolean {
  const iban = raw.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  let rem = 0;
  for (const ch of iban.slice(4) + iban.slice(0, 4)) {
    const code = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of code) rem = (rem * 10 + Number(d)) % 97;
  }
  return rem === 1;
}

export function isValidAbaRouting(raw: string): boolean {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 9) return false;
  const n = [...d].map(Number) as number[];
  return (3 * (n[0]! + n[3]! + n[6]!) + 7 * (n[1]! + n[4]! + n[7]!) + (n[2]! + n[5]! + n[8]!)) % 10 === 0;
}

function check(field: string, value: string, country: string): string | null {
  switch (field) {
    case 'upi_id': return UPI.test(value) ? null : 'Enter a valid UPI ID, e.g. name@okaxis.';
    case 'email': return EMAIL.test(value) ? null : 'Enter a valid email address.';
    case 'ifsc': return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase()) ? null : 'IFSC is 11 characters, e.g. HDFC0001234.';
    case 'iban': return isValidIban(value) ? null : 'That IBAN is not valid (check the digits).';
    case 'swift_bic': return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value.toUpperCase()) ? null : 'SWIFT/BIC is 8 or 11 characters, e.g. HDFCINBBXXX.';
    case 'routing_number': return isValidAbaRouting(value) ? null : 'Enter a valid 9-digit ABA routing number.';
    case 'sort_code': return /^\d{6}$/.test(value.replace(/[-\s]/g, '')) ? null : 'Sort code is 6 digits.';
    case 'bsb': return /^\d{6}$/.test(value.replace(/[-\s]/g, '')) ? null : 'BSB is 6 digits.';
    case 'transit_number': return /^\d{5}$/.test(value) ? null : 'Transit number is 5 digits.';
    case 'institution_number': return /^\d{3}$/.test(value) ? null : 'Institution number is 3 digits.';
    case 'account_type': return /^(checking|savings)$/i.test(value) ? null : 'Enter checking or savings.';
    case 'account_number': {
      const v = value.replace(/\s+/g, '');
      if (country === 'GB') return /^\d{8}$/.test(v) ? null : 'UK account numbers are 8 digits.';
      if (country === 'IN') return /^\d{9,18}$/.test(v) ? null : 'Indian account numbers are 9–18 digits.';
      return /^[A-Za-z0-9]{4,34}$/.test(v) ? null : 'Enter a valid account number.';
    }
    default: return value.length <= 300 ? null : 'Too long.';
  }
}

export interface PayoutDetails { country: string; method: PayoutMethod; holder: string; details: Record<string, string> }

/** Validates payout details for the chosen country and method, strips unknown fields; per-field errors. */
export function validatePayout(input: { country?: unknown; method?: unknown; holder?: unknown; details?: unknown }):
  { ok: true; clean: PayoutDetails } | { ok: false; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const country = String(input.country ?? '').trim().toUpperCase();
  const method = String(input.method ?? '') as PayoutMethod;
  const holder = String(input.holder ?? '').trim().slice(0, 120);
  const validCountry = /^[A-Z]{2}$/.test(country) && ALL_COUNTRIES.includes(country);
  if (!validCountry) errors.country = 'Choose your country.';
  if (!(PAYOUT_METHODS as readonly string[]).includes(method)) errors.method = 'Choose a payout method.';
  else if (validCountry && !methodsForCountry(country).includes(method)) errors.method = 'UPI is available for India only.';
  if (holder.length < 2) errors.holder = 'Enter the full name on the account.';
  if (Object.keys(errors).length) return { ok: false, errors };

  const raw = (input.details && typeof input.details === 'object' ? input.details : {}) as Record<string, unknown>;
  const details: Record<string, string> = {};
  for (const f of payoutFields(method, country)) {
    const value = String(raw[f.key] ?? '').trim();
    if (!value) {
      if (!f.optional) errors[f.key] = `${f.label} is required.`;
      continue;
    }
    const err = check(f.key, value, country);
    if (err) errors[f.key] = err;
    else details[f.key] = ['ifsc', 'iban', 'swift_bic'].includes(f.key) ? value.replace(/\s+/g, '').toUpperCase() : value.slice(0, 300);
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, clean: { country, method, holder, details } };
}

/** Short masked summary for lists, e.g. "Bank (IN) •••• 6789" or "UPI ri•••@okaxis". Never the full number. */
export function payoutSummary(p: Pick<PayoutDetails, 'method' | 'country' | 'details'>): string {
  const d = p.details;
  const tail = (v?: string) => (v ? `•••• ${v.replace(/\s+/g, '').slice(-4)}` : '');
  const maskEmail = (v?: string) => {
    if (!v) return '';
    const [local, domain] = v.split('@');
    return `${(local ?? '').slice(0, 2)}•••@${domain ?? ''}`;
  };
  switch (p.method) {
    case 'upi': return `UPI ${maskEmail(d.upi_id)}`.trim();
    case 'wise': return `Wise ${maskEmail(d.email)}`.trim();
    case 'amazon_gift_card': return `Amazon.com gift card → ${maskEmail(d.email)}`;
    case 'bank': return `Bank (${p.country}) ${tail(d.iban ?? d.account_number)}`.trim();
  }
}
